(function () {
  'use strict';

  // Shopify may evaluate this asset again after a section is added in the editor.
  if (window.MotionGreyOurProcess) {
    window.MotionGreyOurProcess.init(document);
    return;
  }

  const instances = new WeakMap();

  function findRoots(scope) {
    const roots = [...scope.querySelectorAll('[data-mg-process]')];
    if (scope.matches && scope.matches('[data-mg-process]')) roots.unshift(scope);
    return roots;
  }

  function existingKlaviyoLoader() {
    return [...document.scripts].find(script => {
      if (!script.src) return false;
      try {
        const url = new URL(script.src, document.baseURI);
        return /(^|\.)klaviyo\.com$/i.test(url.hostname) &&
          (/\/onsite\/js\//i.test(url.pathname) || /\/media\/js\/onsite\//i.test(url.pathname));
      } catch (error) {
        return false;
      }
    });
  }

  function ensureKlaviyoLoader(companyId) {
    const existing = existingKlaviyoLoader();
    if (existing) return existing;
    const script = document.createElement('script');
    script.id = 'mg-process-klaviyo-onsite';
    script.async = true;
    script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + encodeURIComponent(companyId);
    document.head.appendChild(script);
    return script;
  }

  function initializeRoot(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const tabs = [...root.querySelectorAll('[data-mg-process-tab]')];
    const panels = tabs.map(tab => root.querySelector('#' + CSS.escape(tab.getAttribute('aria-controls'))));
    const processSection = root.querySelector('.mg-process__journey');
    const embed = root.querySelector('[data-mg-process-quote-embed]');
    const loading = root.querySelector('[data-mg-process-quote-loading]');
    const fallback = root.querySelector('[data-mg-process-quote-fallback]');
    const mount = root.querySelector('[data-mg-process-klaviyo-mount]');
    let observer;
    let bodyObserver;
    let timeout;
    let frame;
    let offset = -1;
    let isReady = false;

    function updateHeaderOffset() {
      frame = undefined;
      // Read the native MotionGrey header; never change its classes or positioning.
      const bars = [...document.querySelectorAll('.header-site [data-sticky-pc], .header-site [data-sticky-mb], .header-site .header-mb')];
      let height = 0;
      bars.forEach(bar => {
        const rect = bar.getBoundingClientRect();
        if (!rect.width || !rect.height || rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        // Follow the visible lower edge, including the initial absolute header.
        // A nonsticky header contributes no offset once it leaves the viewport.
        height = Math.max(height, rect.bottom);
      });
      const nextOffset = Math.ceil(height);
      if (nextOffset === offset) return;
      offset = nextOffset;
      root.style.setProperty('--mg-process-header-offset', nextOffset + 'px');
    }

    function queueHeaderOffset() {
      if (frame === undefined) frame = requestAnimationFrame(updateHeaderOffset);
    }

    function selectStep(index, focus) {
      if (!tabs[index] || panels.some(panel => !panel)) return;
      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        panels[tabIndex].hidden = !selected;
      });
      if (focus) tabs[index].focus();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectStep(index), { signal });
      tab.addEventListener('keydown', event => {
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === undefined) return;
        event.preventDefault();
        selectStep(nextIndex, true);
      }, { signal });
    });

    function selectLinkedStep() {
      let hash;
      try { hash = decodeURIComponent(window.location.hash.slice(1)); } catch (error) { return; }
      const index = panels.findIndex(panel => panel && panel.id === hash);
      if (index < 0) return;
      selectStep(index);
      updateHeaderOffset();
      processSection.scrollIntoView({ block: 'start' });
    }

    function showFallback() {
      if (signal.aborted || isReady || !embed || !loading || !fallback) return;
      loading.hidden = true;
      fallback.hidden = false;
      embed.classList.add('mg-process__is-unavailable');
    }

    function checkForm() {
      if (!mount || !mount.querySelector('input:not([type="hidden"]), textarea, select')) return false;
      isReady = true;
      loading.hidden = true;
      fallback.hidden = true;
      embed.classList.remove('mg-process__is-unavailable');
      embed.classList.add('mg-process__is-ready');
      clearTimeout(timeout);
      if (observer) observer.disconnect();
      return true;
    }

    if (embed && loading && fallback && mount) {
      const formId = (root.dataset.klaviyoFormId || 'UumyQJ').trim();
      const companyId = (root.dataset.klaviyoCompanyId || 'XpuL4H').trim();
      if (/^[a-z0-9]+$/i.test(formId) && /^[a-z0-9]+$/i.test(companyId)) {
        observer = new MutationObserver(checkForm);
        observer.observe(mount, { childList: true, subtree: true });
        if (!checkForm()) timeout = setTimeout(showFallback, 15000);
        const loader = ensureKlaviyoLoader(companyId);
        loader.addEventListener('error', showFallback, { signal });
      } else {
        showFallback();
      }
    }

    window.addEventListener('hashchange', selectLinkedStep, { signal });
    window.addEventListener('scroll', queueHeaderOffset, { passive: true, signal });
    window.addEventListener('resize', queueHeaderOffset, { passive: true, signal });
    bodyObserver = new MutationObserver(queueHeaderOffset);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    updateHeaderOffset();
    selectLinkedStep();

    instances.set(root, function destroy() {
      controller.abort();
      clearTimeout(timeout);
      if (frame !== undefined) cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      if (bodyObserver) bodyObserver.disconnect();
      instances.delete(root);
    });
  }

  function init(scope) {
    findRoots(scope).forEach(initializeRoot);
  }

  window.MotionGreyOurProcess = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    findRoots(event.target).forEach(root => {
      const destroy = instances.get(root);
      if (destroy) destroy();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
